/*
Reusable method used to display formatted date time in EventDetails
*/

const locale = "en-US";

const fmt = (date: Date, config: Intl.DateTimeFormatOptions) =>
  new Intl.DateTimeFormat(locale, config).format(date);

const fmtParts = (date: Date, config: Intl.DateTimeFormatOptions) =>
  new Intl.DateTimeFormat(locale, config).formatToParts(date);

const longDate = (date: Date) =>
  fmt(date, { month: "long", day: "numeric", year: "numeric" });

const timeAmPm = (date: Date) => {
  const parts = fmtParts(date, {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

  const hour = parts.find((p) => p.type === "hour")?.value ?? "";
  const minute = parts.find((p) => p.type === "minute")?.value ?? "";
  const period = parts.find((p) => p.type === "dayPeriod")?.value ?? "";
  const ampm = period.toLowerCase() === "am" ? "a.m." : "p.m.";

  return hour ? `${hour}:${minute} ${ampm}` : "";
};

const getYMD = (date: Date) => {
  const parts = fmtParts(date, {
    year: "numeric",
    month: "numeric",
    day: "numeric",
  });

  return {
    y: Number(parts.find((p) => p.type === "year")?.value),
    m: Number(parts.find((p) => p.type === "month")?.value),
    d: Number(parts.find((p) => p.type === "day")?.value),
  };
};

const isSameCalendarDay = (start: Date, end: Date) => {
  const { y: sy, m: sm, d: sd } = getYMD(start);
  const { y: ey, m: em, d: ed } = getYMD(end);

  return sy === ey && sm === em && sd === ed;
};

/* -----------------------------
   Case 3: Multi-day, same year
------------------------------*/
const formatSameYearDateRange = (
  sMonthLong: string,
  eMonthLong: string,
  sDay: string,
  eDay: string,
  sYear: string,
) => {
  if (sMonthLong === eMonthLong) {
    return `${sMonthLong} ${sDay}–${eDay}, ${sYear}`;
  }

  return `${sMonthLong} ${sDay} – ${eMonthLong} ${eDay}, ${sYear}`;
};

/* -----------------------------
   Case 4: Cross-year
------------------------------*/
const formatCrossYearDateRange = (
  sMonthLong: string,
  eMonthLong: string,
  sDay: string,
  eDay: string,
  sYear: string,
  eYear: string,
) => `${sMonthLong} ${sDay}, ${sYear} – ${eMonthLong} ${eDay}, ${eYear}`;

const formatMultiDayDate = (start: Date, end: Date) => {
  const sMonthLong = fmt(start, { month: "long" });
  const eMonthLong = fmt(end, { month: "long" });
  const sDay = fmt(start, { day: "numeric" });
  const eDay = fmt(end, { day: "numeric" });
  const sYear = fmt(start, { year: "numeric" });
  const eYear = fmt(end, { year: "numeric" });

  /* -----------------------------
     Case 3: Multi-day, same year
  ------------------------------*/
  if (sYear === eYear) {
    return formatSameYearDateRange(sMonthLong, eMonthLong, sDay, eDay, sYear);
  }

  /* -----------------------------
     Case 4: Cross-year
  ------------------------------*/
  return formatCrossYearDateRange(
    sMonthLong,
    eMonthLong,
    sDay,
    eDay,
    sYear,
    eYear,
  );
};

export function formatEventDate(
  startISO: string | Date | null | undefined,
  endISO?: string | Date | null,
  options?: { includeTime?: boolean },
): string {
  const includeTime = options?.includeTime ?? true;

  if (!startISO) return "";

  const start =
    startISO instanceof Date ? startISO : new Date(startISO.replace("Z", ""));

  if (Number.isNaN(start.getTime())) return "";

  const end =
    endISO &&
    (endISO instanceof Date ? endISO : new Date(endISO.replace("Z", "")));

  if (end && Number.isNaN(end.getTime())) return "";

  /* -----------------------------
     Case 1: End date NOT provided
     Example (includeTime): May 5, 2025 2:00 p.m.
     Example (date only):  May 5, 2025
  ------------------------------*/
  if (!end) {
    if (!includeTime) return longDate(start);

    const time = timeAmPm(start);
    return time ? `${longDate(start)} | ${time}` : longDate(start);
  }

  /* -----------------------------
     Case 2: Same calendar day
     Example (includeTime): May 5, 2025 2:00 p.m. – 4:00 p.m.
     Example (date only):  May 5, 2025
  ------------------------------*/
  if (isSameCalendarDay(start, end)) {
    if (!includeTime) return longDate(start);
    return `${longDate(start)} | ${timeAmPm(start)} – ${timeAmPm(end)}`;
  }

  return formatMultiDayDate(start, end);
}
